import { FC, useEffect, useRef, useContext, useState } from 'react'
import { Button } from '@fluentui/react-components'
import cx from 'classnames'
import { TeamsFxContext } from '../Context'

import './ApplicationFrame.css'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface ApplicationFrameProps {
  domain: string
  timesheet: boolean
  onLogout: () => void
}

export const ApplicationFrame: FC<ApplicationFrameProps> = ({ domain, timesheet, onLogout }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { themeString } = useContext(TeamsFxContext)
  const [src, setSrc] = useState<string>(`https://${domain}/sciforma/#Timesheet`)
  const [searchParams] = useSearchParams()
  const displayUrl = atob(searchParams.get('Url') ?? '')
  const { t } = useTranslation()

  // Set the src to be displayed, should it be the timesheet or a Grid/Board
  useEffect(() => {
    setSrc((timesheet) ? `https://${domain}/sciforma/#Timesheet` : displayUrl ?? '')
  }, [domain, displayUrl, timesheet])

  // When loading the frame, check the health of the backend to see if we are still logged in
  useEffect(() => {
    if (!domain) return;
    const checkInstanceHealth = async () => {
      try {
        const response = await fetch(`https://${domain}/sciforma/rest/health`, {
          method: 'GET',
          credentials: 'include'
        });
        console.log('Health response', response);
        if (response.ok) {
          const body = await response.json();
          if (body.status !== 'UP') {
            onLogout();
          }
        } else {
          onLogout();
        }
      } catch (error) {
        console.error('Health check failed:', error);
        onLogout();
      }
    };

    checkInstanceHealth()
    // every 5 minutes check if it's logged out
    const intervalId = setInterval(() => {
      console.log("Health check from timer")
      checkInstanceHealth()
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [domain, onLogout]);

  // This is meant to handle when a session expires in the iframe, not sure it works though
  useEffect(() => {
    const listener = (e: any): void => {
      try {
        const messageData = JSON.parse(e.data)

        if (messageData.method === 'sessionExpired') {
          console.log('[RECEIVED MESSAGE FROM FRAME]', e)
          onLogout()
        }
      } catch (error) {
        console.error('Error', error)
      }
    }

    window.addEventListener('message', listener)

    return () => {
      window?.removeEventListener('message', listener)
    }
  }, [iframeRef.current])

  // Show the correct header class depending on which frame is being displayed
  const getHeaderClass = (): string => {
    if (src.trim().toLowerCase().includes('#timesheet')) {
      return 'application-headerTimesheet'
    } else {
      return 'application-header'
    }
  }

  return (
    <main className='application-frame'>
      <div className={getHeaderClass()}>
        <Button className={cx({ button: themeString === 'dark' || themeString === 'contrast' })} onClick={onLogout}>{t('LOGOUT')}</Button>
      </div>

      <iframe
        ref={iframeRef}
        className='sciforma-frame'
        title='sciforma-frame'
        src={src}
      />
    </main>
  )
}
